<script setup lang="ts">
import maplibregl from 'maplibre-gl'
import { inject, onUnmounted, watch, type ShallowRef } from 'vue'

/**
 * Live overlay for the in-progress track recording. Shared layer (no scope prefix): it renders in
 * every map context while a recording session exists. Purely passive — it never moves the camera,
 * so follow-location / navigation keep working untouched while recording.
 *
 * Visual is deliberately distinct from tour and drawing lines: a solid recording-red line
 * (matching the recording FAB / modal accents) over a white casing, plus a red head dot at the
 * most recent fix. One line per recording segment, so pauses show as gaps.
 */

const props = defineProps<{
  segments: [number, number][][] // per-segment [lon, lat] coordinates
}>()

const mapInstance = inject<ShallowRef<maplibregl.Map | null>>('mapInstance')
const SOURCE_ID = 'recording-track-source'
const HEAD_SOURCE_ID = 'recording-track-head-source'
const CASING_LAYER_ID = 'recording-track-casing'
const LINE_LAYER_ID = 'recording-track-line'
const HEAD_LAYER_ID = 'recording-track-head'

const RECORDING_COLOR = '#dc2626' // red-600, same family as the recording FAB/modal accents

const buildLineData = (): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features: props.segments
    .filter((segment) => segment.length > 1)
    .map((segment) => ({
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: segment },
    })),
})

const buildHeadData = (): GeoJSON.FeatureCollection => {
  const lastSegment = props.segments[props.segments.length - 1]
  const head = lastSegment?.[lastSegment.length - 1]
  return {
    type: 'FeatureCollection',
    features: head
      ? [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: head } }]
      : [],
  }
}

const updateLayers = () => {
  if (!mapInstance?.value) return
  const map = mapInstance.value

  const lineSource = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined
  const headSource = map.getSource(HEAD_SOURCE_ID) as maplibregl.GeoJSONSource | undefined

  if (lineSource && headSource) {
    lineSource.setData(buildLineData())
    headSource.setData(buildHeadData())
    return
  }

  map.addSource(SOURCE_ID, { type: 'geojson', data: buildLineData() })
  map.addSource(HEAD_SOURCE_ID, { type: 'geojson', data: buildHeadData() })

  map.addLayer({
    id: CASING_LAYER_ID,
    type: 'line',
    source: SOURCE_ID,
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': '#ffffff', 'line-width': 8, 'line-opacity': 0.9 },
  })

  map.addLayer({
    id: LINE_LAYER_ID,
    type: 'line',
    source: SOURCE_ID,
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: { 'line-color': RECORDING_COLOR, 'line-width': 4 },
  })

  map.addLayer({
    id: HEAD_LAYER_ID,
    type: 'circle',
    source: HEAD_SOURCE_ID,
    paint: {
      'circle-radius': 6,
      'circle-color': RECORDING_COLOR,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 2,
    },
  })
}

watch(() => props.segments, updateLayers, { deep: true })

watch(
  () => mapInstance?.value,
  (map) => {
    if (!map) return
    if (map.loaded()) {
      updateLayers()
    } else {
      map.on('load', updateLayers)
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  if (!mapInstance?.value) return
  const map = mapInstance.value
  for (const layerId of [HEAD_LAYER_ID, LINE_LAYER_ID, CASING_LAYER_ID]) {
    if (map.getLayer(layerId)) map.removeLayer(layerId)
  }
  for (const sourceId of [HEAD_SOURCE_ID, SOURCE_ID]) {
    if (map.getSource(sourceId)) map.removeSource(sourceId)
  }
})
</script>

<template>
  <slot></slot>
</template>
